import { Bot, User } from 'lucide-react';

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  metadata?: Record<string, unknown>;
  timestamp?: string;
}

function formatMarkdown(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code class="bg-theme-surface px-1 py-0.5 rounded text-sm">$1</code>')
    .replace(/\n- /g, '\n<br/>• ')
    .replace(/\n(\d+)\. /g, '\n<br/>$1. ')
    .replace(/\n/g, '<br/>');
}

export default function ChatMessage({ role, content, metadata, timestamp }: ChatMessageProps) {
  const isUser = role === 'user';

  return (
    <div className={`flex gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      <div
        className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
          isUser ? 'bg-brand text-white' : 'bg-theme-surface text-theme-secondary'
        }`}
      >
        {isUser ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
      </div>

      <div className={`max-w-[80%] ${isUser ? 'text-right' : 'text-left'}`}>
        <div
          className={`inline-block rounded-xl px-3 py-2 text-sm ${
            isUser
              ? 'bg-brand text-white rounded-tr-sm'
              : 'bg-theme-surface text-theme-primary rounded-tl-sm'
          }`}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap">{content}</p>
          ) : (
            <div
              className="prose prose-sm max-w-none dark:prose-invert [&>br:first-child]:hidden"
              dangerouslySetInnerHTML={{ __html: formatMarkdown(content) }}
            />
          )}
        </div>

        <div className="flex items-center gap-1.5 mt-0.5 px-1">
          {timestamp && (
            <span className="text-[10px] text-theme-tertiary">
              {new Date(timestamp).toLocaleTimeString('nl-NL', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          )}
          {!isUser && typeof metadata?.model === 'string' && (
            <span className="text-[10px] text-theme-tertiary">
              {metadata.model.includes('opus') ? 'Opus' : 'Sonnet'}
            </span>
          )}
          {!isUser &&
            Array.isArray(metadata?.tools_used) &&
            (metadata.tools_used as string[]).length > 0 && (
              <span className="text-[10px] text-theme-tertiary">
                {(metadata.tools_used as string[]).length} tool
                {(metadata.tools_used as string[]).length > 1 ? 's' : ''} gebruikt
              </span>
            )}
        </div>
      </div>
    </div>
  );
}
